/**
 * RunKanbanBoard — three-lane kanban for the run-fidelity view.
 *
 * Groups RunOverviewEntry[] into Running / Stuck / Done lanes using
 * runLaneForState(). Each card is a RunSparklineRow in list mode — the same
 * component used in the flat run list, so layout stays consistent. Three lanes
 * (not five) is the honest model at run granularity: a run's trials live in
 * multiple statuses simultaneously, so trial-lane buckets are meaningless here.
 *
 * Purely presentational — no fetch, no polling, no global reads.
 */

import type { JSX } from 'react'
import { RunSparklineRow } from './RunSparklineRow'
import type { CancelRunResult } from './board-client'
import { type RunLane, runLaneForState } from './pipeline-data'
import type { RunOverviewEntry } from './types'

const RUN_LANE_ORDER: RunLane[] = ['running', 'stuck', 'done']

const RUN_LANE_LABELS: Record<RunLane, string> = {
  running: 'Running',
  stuck: 'Stuck',
  done: 'Done',
}

export interface RunKanbanBoardProps {
  /** Already filtered/scoped runs to display. */
  runs: RunOverviewEntry[]
  /** Fires a run cancel; threaded to each row's Cancel control (live runs only). */
  onCancelRun?: (runId: string) => Promise<CancelRunResult>
  /** Called once a cancel succeeds, so the parent can re-fetch the board. */
  onRunCancelled?: () => void
}

export function RunKanbanBoard({ runs, onCancelRun, onRunCancelled }: RunKanbanBoardProps): JSX.Element {
  // Bucket runs into their three lanes, preserving incoming order within each.
  const grouped: Record<RunLane, RunOverviewEntry[]> = { running: [], stuck: [], done: [] }
  for (const run of runs) {
    grouped[runLaneForState(run)].push(run)
  }

  return (
    <div className="kanban-board run-kanban-board" data-testid="run-kanban-board" aria-label="Run board by state">
      {RUN_LANE_ORDER.map((lane) => {
        const laneRuns = grouped[lane]
        return (
          <section
            key={lane}
            className={`kanban-lane kanban-lane-${lane}`}
            data-testid={`run-kanban-lane-${lane}`}
            data-count={laneRuns.length}
          >
            <header className="kanban-lane-header">
              <span className="kanban-lane-label">{RUN_LANE_LABELS[lane]}</span>
              <span className="kanban-lane-count" data-testid={`run-kanban-lane-count-${lane}`}>
                {laneRuns.length}
              </span>
            </header>
            {laneRuns.length === 0 ? (
              <p className="kanban-lane-empty" data-testid={`run-kanban-lane-empty-${lane}`}>
                —
              </p>
            ) : (
              <ul className="kanban-lane-list">
                {laneRuns.map((run) => (
                  <RunSparklineRow
                    key={run.runId}
                    run={run}
                    throughputSeries={[]}
                    onCancelRun={onCancelRun}
                    onRunCancelled={onRunCancelled}
                  />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
